import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import Select from 'react-select';
import Sidebar from '../components/Sidebarpaiment';
import ImportPaiementsExcel from '../components/ImportPaiementsExcel';
import {
  Save, UserRoundSearch, BookOpen, Calendar, BadgeEuro,
  StickyNote, Info, AlertCircle, CreditCard, History, ChevronDown, ChevronUp, Download, RefreshCw, FileText
} from 'lucide-react';
import { jsPDF } from "jspdf";
import { nombreEnLettres } from '../utils/numberToWords';
import { SUP2I_LOGO_BASE64 } from '../assets/sup2iLogo';

const API_BASE_URL = ' https://vmi1977988.contaboserver.net/api2';

const handleLogout = () => {
  localStorage.removeItem('token');
  window.location.href = '/';
};

const fmt = (n) =>
  new Intl.NumberFormat('fr-MA', { minimumFractionDigits: 0 }).format(n ?? 0) + ' MAD';

const ANNEES = ['2023/2024', '2024/2025', '2025/2026', '2026/2027', '2027/2028'];

const modePaymentOptions = [
  { value: 'Espèce', label: 'Espèce' },
  { value: 'Chèque encaissé', label: 'Chèque encaissé' },
  { value: 'Versement', label: 'Versement' },
  { value: 'Virement', label: 'Virement' },
  { value: 'Chèque non encaissé', label: 'Chèque non encaissé' },
];

// ─── Historique dépliable ─────────────────────────────────────────────────────
const HistoriqueEtudiant = ({ codeEtudiant, getAuthConfig, anneeCourante }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open || !codeEtudiant) return;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${API_BASE_URL}/paiements/historique/${codeEtudiant}`,
          getAuthConfig()
        );
        setData(res.data);
      } catch { setData(null); }
      finally { setLoading(false); }
    };
    load();
  }, [open, codeEtudiant, getAuthConfig]);

  if (!codeEtudiant) return null;

  return (
    <div style={{ marginTop: 20, border: '1px solid #e0f2fe', borderRadius: 10, overflow: 'hidden' }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px', background: open ? '#e0f2fe' : '#f0f9ff',
        border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#0369a1',
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <History size={16} /> Voir l'historique complet des paiements
        </span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {open && (
        <div style={{ padding: '16px', background: '#f8fafc' }}>
          {loading ? (
            <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Chargement...</div>
          ) : !data ? (
            <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Aucune donnée disponible</div>
          ) : (
            <>
              {data.detteGlobale > 0 && (
                <div style={{
                  background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
                  padding: '8px 14px', marginBottom: 12, fontSize: 12,
                  color: '#dc2626', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  <AlertCircle size={14} />
                  Dette totale toutes années : {fmt(data.detteGlobale)}
                </div>
              )}
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {data.historique.map(h => {
                  const isCurrent = h.anneeScolaire === anneeCourante;
                  const pct = h.prixTotal > 0
                    ? Math.min(100, Math.round((h.totalPaye / h.prixTotal) * 100)) : 0;
                  const color = pct === 100 ? '#059669' : pct >= 60 ? '#d97706' : '#dc2626';
                  return (
                    <div key={h.anneeScolaire} style={{
                      background: 'white',
                      border: isCurrent ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                      borderRadius: 10, padding: '12px 16px',
                      flex: '1 1 200px', maxWidth: 280,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                        <span style={{
                          fontSize: 12, fontWeight: 700,
                          color: isCurrent ? '#1d4ed8' : '#374151',
                          background: isCurrent ? '#eff6ff' : '#f3f4f6',
                          padding: '2px 8px', borderRadius: 5,
                        }}>
                          {h.anneeScolaire}{isCurrent ? ' ★' : ''}
                        </span>
                        <span style={{ fontSize: 11, fontWeight: 600, color: pct === 100 ? '#059669' : '#dc2626' }}>
                          {pct}%
                        </span>
                      </div>
                      <div style={{ height: 5, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden', marginBottom: 8 }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3 }} />
                      </div>
                      <div style={{ fontSize: 11, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                        <div style={{ color: '#6b7280' }}>Total : <strong>{fmt(h.prixTotal)}</strong></div>
                        <div style={{ color: '#6b7280' }}>Payé : <strong style={{ color: '#059669' }}>{fmt(h.totalPaye)}</strong></div>
                        {h.reste > 0 && (
                          <div style={{ gridColumn: '1/-1', color: '#6b7280' }}>
                            Reste : <strong style={{ color: '#dc2626' }}>{fmt(h.reste)}</strong>
                          </div>
                        )}
                      </div>
                      {h.paiementsFormation?.length > 0 && (
                        <div style={{ marginTop: 8, borderTop: '1px solid #f3f4f6', paddingTop: 6 }}>
                          {h.paiementsFormation.map((p, i) => (
                            <div key={i} style={{
                              display: 'flex', justifyContent: 'space-between',
                              fontSize: 11, color: '#374151', padding: '2px 0',
                              borderBottom: '1px dashed #f3f4f6',
                            }}>
                              <span style={{ color: '#6b7280' }}>
                                {p.datePaiement
                                  ? new Date(p.datePaiement).toLocaleDateString('fr-MA')
                                  : new Date(p.createdAt).toLocaleDateString('fr-MA')}
                                {p.numeroSerie && <span style={{ marginLeft: 5, color: '#9ca3af' }}>#{p.numeroSerie}</span>}
                              </span>
                              <strong style={{ color: '#059669' }}>{fmt(p.montant)}</strong>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Page principale ──────────────────────────────────────────────────────────
const AjouterPaiement = () => {
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    const loggedInUser = userStr ? JSON.parse(userStr) : null;
    if (loggedInUser?.email?.toLowerCase() === 'hiba@supemir.com') {
      window.location.href = '/manager/listePaiement';
    }
  }, []);

  const [anneeFiltre, setAnneeFiltre] = useState('2025/2026');

  // ── Liste étudiants pour le Select ──
  // On garde deux listes séparées :
  //   etudiantsDirects  → ceux dont anneeScolaire === anneeFiltre  (paiement direct)
  //   etudiantsTransferes → ceux qui ont été transférés mais ont une dette sur anneeFiltre
  const [etudiantOptions, setEtudiantOptions] = useState([]);
  const [etudiantsComplets, setEtudiantsComplets] = useState({}); // { _id: etudiantObj }

  const [cours, setCours] = useState([]);

  // Infos de l'étudiant sélectionné
  const [prixTotalEtudiant, setPrixTotalEtudiant] = useState(0);
  const [totalDejaPaye, setTotalDejaPaye] = useState(0);
  const [resteAPayer, setResteAPayer] = useState(0);
  const [modePaiementEtudiant, setModePaiementEtudiant] = useState('');
  const [infosModesPaiement, setInfosModesPaiement] = useState(null);
  const [fraisInscription, setFraisInscription] = useState(0);
  const [codeEtudiantSelectionne, setCodeEtudiantSelectionne] = useState('');
  const [anneeScolaireEtudiant, setAnneeScolaireEtudiant] = useState('');
  // L'ID MongoDB à utiliser pour enregistrer le paiement (peut être différent si transféré)
  const [etudiantIdPaiement, setEtudiantIdPaiement] = useState('');

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    etudiant: '',      // valeur affichée dans le Select (peut être "code:annee")
    cours: [],
    moisDebut: '',
    nombreMois: 1,
    montant: '',
    note: '',
    typePaiement: 'formation',
    estInscription: false,
    numeroSerie: 'sup00001',
    numeroSerieManuel: '',
    modePayment: null,
    banque: '',
    numeroCheque: '',
    datePaiement: new Date().toISOString().split('T')[0],
  });

  const [message, setMessage] = useState({ text: '', type: '' });
  const [showRappelModal, setShowRappelModal] = useState(false);
  const [rappelData, setRappelData] = useState({ note: '', dateRappel: '' });

  const getAuthConfig = useCallback(() => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  }, []);

  const showMessage = useCallback((text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  }, []);

  const fetchProchainNumeroSerie = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/paiements/prochain-numero-serie`);
      if (res.data?.numeroSerie) {
        setForm(prev => ({ ...prev, numeroSerie: res.data.numeroSerie }));
      }
    } catch (err) {
      console.error('Erreur récupération numéro de série:', err);
    }
  }, []);

  useEffect(() => {
    fetchProchainNumeroSerie();
  }, [fetchProchainNumeroSerie]);

  // ✅ Vrai statut: seulement si un étudiant est sélectionné ET qu'il a réellement soldé sa formation
  const isTotalementPaye = Boolean(
    form.etudiant && !form.estInscription && resteAPayer <= 0 && prixTotalEtudiant > 0
  );

  const [dernierPaiement, setDernierPaiement] = useState(null);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatEditorName = (name, userObj) => {
    if (userObj) {
      const p = (userObj.prenom || userObj.firstName || '').trim();
      const n = (userObj.nom || userObj.nomDeFamille || userObj.lastName || '').trim();
      if (p && n && p !== n) {
        return (p[0] + n[0]).toUpperCase();
      }
    }

    if (!name) return 'MS';

    let clean = name.trim();
    if (clean.includes('@')) {
      clean = clean.split('@')[0];
    }
    clean = clean.replace(/[._-]+/g, ' ').trim();

    const parts = clean.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    if (parts.length === 1) {
      const single = parts[0];
      if (single.length >= 2) {
        return single.slice(0, 2).toUpperCase();
      }
      return single.toUpperCase();
    }
    return 'MS';
  };

  const generatePDF = (p) => {
    // Format A4 Portrait (210 x 297 mm) pour contenir 2 exemplaires sur 1 seule page
    const doc = new jsPDF('portrait', 'mm', 'a4');
    let receiptNum = p.numeroSerie || (p._id ? p._id.toString().slice(-6) : `00${Date.now().toString().slice(-4)}`);

    const renderReceipt = (yOffset = 0) => {
      // Couleurs officielles SUP2I
      const primary = [15, 44, 89];      // #0F2C59 Bleu Nuit officiel SUP2I
      const royalBlue = [27, 85, 175];   // #1B55AF Bleu Royal
      const accentRed = [220, 38, 38];    // #DC2626 Rouge Reçu N° et Avertissement
      const textDark = [31, 41, 55];      // #1F2937 Texte sombre
      const textMuted = [107, 114, 128];  // #6B7280 Texte gris
      const lineGrey = [209, 213, 219];   // #D1D5DB Ligne discrète
      const bgLight = [248, 250, 252];    // #F8FAFC Fond clair

      // 1. Cadre général professionnel (double filet élégant)
      doc.setDrawColor(...primary);
      doc.setLineWidth(1.1);
      doc.roundedRect(4, 4 + yOffset, 202, 92, 2, 2, 'D');

      doc.setDrawColor(...royalBlue);
      doc.setLineWidth(0.3);
      doc.roundedRect(5.2, 5.2 + yOffset, 199.6, 89.6, 1.5, 1.5, 'D');

      // 2. LOGO OFFICIEL SUP2I
      try {
        doc.addImage(SUP2I_LOGO_BASE64, 'PNG', 8, 6.5 + yOffset, 46, 15.5);
      } catch (e) {
        doc.setTextColor(...primary);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(16);
        doc.text('SUP2I', 21, 14 + yOffset);
        doc.setTextColor(...textMuted);
        doc.setFontSize(7.5);
        doc.text("Reconnue par l'État", 21, 18.5 + yOffset);
      }

      // Bloc en-tête droit : REÇU DE PAIEMENT
      doc.setTextColor(...primary);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.text('REÇU DE PAIEMENT', 152, 10 + yOffset, { align: 'center' });

      // Année universitaire
      const anneeUniv = p.etudiant?.anneeScolaire || anneeFiltre || '2025/2026';
      doc.setTextColor(...royalBlue);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      doc.text(`Année universitaire ${anneeUniv}`, 152, 14 + yOffset, { align: 'center' });

      // Reçu N°
      doc.setTextColor(...textDark);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text('Reçu N° : ', 138, 18.2 + yOffset);
      doc.setTextColor(...accentRed);
      doc.setFontSize(10.5);
      doc.text(`${receiptNum}`, 154, 18.2 + yOffset);

      if (p.numeroSerieManuel) {
        doc.setTextColor(55, 65, 81);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.text(`${p.numeroSerieManuel}`, 154, 22.2 + yOffset);
      }

      // Ligne de séparation sous l'en-tête
      doc.setDrawColor(...royalBlue);
      doc.setLineWidth(0.6);
      doc.line(8, 24 + yOffset, 202, 24 + yOffset);

      // Données de l'étudiant
      const studentName = (
        p.etudiant?.nomComplet ||
        (p.etudiant ? `${p.etudiant.nomDeFamille || ''} ${p.etudiant.prenom || ''}`.trim() : '') ||
        '_________________________________'
      ).toUpperCase();

      const montantVal = Number(p.montant) || 0;
      const montantLettres = nombreEnLettres(montantVal);

      // --- Ligne 1 : Reçu de: Mr/Mme ---
      let y = 29.5 + yOffset;
      doc.setTextColor(...textDark);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text('Reçu de: Mr/Mme', 10, y);

      doc.setTextColor(...primary);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text(studentName, 43, y);

      // Ligne pointillée
      doc.setDrawColor(...lineGrey);
      doc.setLineWidth(0.3);
      doc.setLineDashPattern([0.8, 0.8], 0);
      doc.line(43, y + 1.2, 200, y + 1.2);
      doc.setLineDashPattern([], 0); // reset

      // --- Ligne 2 : La somme de : ---
      y = 36.5 + yOffset;
      doc.setTextColor(...textDark);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text('La somme de :', 10, y);

      doc.setTextColor(17, 24, 39);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const sommeComplete = `${montantLettres} (${montantVal} DH)`;
      doc.text(sommeComplete, 38, y);

      doc.setDrawColor(...lineGrey);
      doc.setLineWidth(0.3);
      doc.setLineDashPattern([0.8, 0.8], 0);
      doc.line(38, y + 1.2, 200, y + 1.2);
      doc.setLineDashPattern([], 0);

      // --- Bloc intermédiaire (En règlement des VS Observation) ---
      const isInscription = Boolean(
        p.estInscription ||
        p.typePaiement === 'inscription' ||
        (p.note && p.note.toLowerCase().includes('inscription'))
      );
      const isScolarite = !isInscription;

      // Colonne gauche : En règlement des
      doc.setTextColor(...textDark);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      doc.text('En règlement des :', 10, 45 + yOffset);

      // Helper pour case à cocher
      const drawBoxWithCheck = (x, boxY, checked, label) => {
        doc.setDrawColor(...primary);
        doc.setLineWidth(0.4);
        doc.rect(x, boxY, 3.2, 3.2);

        if (checked) {
          doc.setDrawColor(...royalBlue);
          doc.setLineWidth(0.7);
          // Checkmark
          doc.line(x + 0.6, boxY + 1.8, x + 1.3, boxY + 2.7);
          doc.line(x + 1.3, boxY + 2.7, x + 2.8, boxY + 0.6);
        }

        doc.setTextColor(...textDark);
        doc.setFont('helvetica', checked ? 'bold' : 'normal');
        doc.setFontSize(8);
        doc.text(label, x + 4.8, boxY + 2.6);
      };

      drawBoxWithCheck(43, 43.5 + yOffset, isInscription, "Frais d'inscription");
      drawBoxWithCheck(43, 50.5 + yOffset, isScolarite, 'Frais de scolarité');

      // Colonne droite : Cadre Observation
      doc.setDrawColor(...primary);
      doc.setLineWidth(0.5);
      doc.setFillColor(...bgLight);
      doc.roundedRect(108, 40.5 + yOffset, 92, 17, 1.5, 1.5, 'FD');

      doc.setTextColor(...primary);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.text('Observation :', 111, 44.5 + yOffset);

      // Ligne 1 observation : Niveau & Filière
      const niveau = p.etudiant?.niveau ? `${p.etudiant.niveau}${p.etudiant.niveau === 1 ? 'ère' : 'ème'} Année` : '';
      const filiere = p.etudiant?.filiere || '';
      const coursStr = Array.isArray(p.cours) ? p.cours.join(', ') : (p.cours || '');
      const filiereInfo = [niveau, filiere, coursStr].filter(Boolean).join(' - ');

      doc.setTextColor(...textDark);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.text(filiereInfo.length > 52 ? filiereInfo.substring(0, 52) + '...' : filiereInfo, 111, 49.5 + yOffset);

      // Ligne 2 observation : Période / Tranche / Note
      let obsSub = '';
      if (p.note && p.note.trim() !== '') {
        obsSub = p.note;
      } else if (p.moisDebut) {
        obsSub = `Période: ${formatDate(p.moisDebut)} (${p.nombreMois || 1} mois)`;
      }
      if (obsSub) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        doc.setTextColor(...textMuted);
        doc.text(obsSub.length > 55 ? obsSub.substring(0, 55) + '...' : obsSub, 111, 54.5 + yOffset);
      }

      // --- Ligne 4 : Mode de règlement ---
      y = 61.5 + yOffset;
      doc.setTextColor(...textDark);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.text('Mode de règlement :', 8.5, y);

      const modeStr = (p.modePayment || p.modePaiement || '').toLowerCase();
      const isEsp = modeStr.includes('esp') || (!modeStr && true);
      const isVir = modeStr.includes('vir');
      const isCheq = modeStr.includes('ch');
      const isVers = modeStr.includes('vers');

      // Récupération ou extraction banque & numéro de chèque si disponible
      let bqVal = p.banque || '';
      let numVal = p.numeroCheque || p.numCheque || '';
      const combinedText = `${p.note || ''} ${p.observationFinance || ''}`.trim();
      if (!bqVal && combinedText) {
        const bqMatch = combinedText.match(/(?:banque|bq)[:\s]+([A-Za-z0-9\s]+?)(?:[-,\n]|$|n[°o])/i) ||
          combinedText.match(/\b(attijari(?:wafa)?|bmce|boa|cih|chaabi|banque populaire|bp|sgmb|societe generale|cdg|credit agricole|cam|bmci)\b/i);
        if (bqMatch) bqVal = bqMatch[1].trim();
      }
      if (!numVal && combinedText) {
        const numMatch = combinedText.match(/(?:n[°o]|num[eé]ro|ch[eè]que\s*n[°o]?)[:\s]*([A-Za-z0-9]+)/i);
        if (numMatch) numVal = numMatch[1].trim();
      }

      let currX = 38.5;
      doc.setFontSize(7.5);

      // 1. Espèces
      drawBoxWithCheck(currX, y - 2.5, isEsp, 'Espèces');
      doc.setFont('helvetica', isEsp ? 'bold' : 'normal');
      currX += 4.5 + doc.getTextWidth('Espèces') + 3.5;

      // 2. Virement : Date
      drawBoxWithCheck(currX, y - 2.5, isVir, 'Virement');
      doc.setFont('helvetica', isVir ? 'bold' : 'normal');
      currX += 4.5 + doc.getTextWidth('Virement') + 1.2;

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...textDark);
      doc.text('Date :', currX, y);
      currX += doc.getTextWidth('Date :') + 1;

      const virDateStr = isVir ? formatDate(p.dateVirement || p.datePaiement || p.createdAt) : '...........';
      doc.setFont('helvetica', isVir ? 'bold' : 'normal');
      doc.setTextColor(...(isVir ? primary : textMuted));
      doc.text(virDateStr, currX, y);
      currX += doc.getTextWidth(virDateStr) + 3.5;

      // 3. Chèque : Banque & N°
      drawBoxWithCheck(currX, y - 2.5, isCheq, 'Chèque');
      doc.setFont('helvetica', isCheq ? 'bold' : 'normal');
      currX += 4.5 + doc.getTextWidth('Chèque') + 1.2;

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...textDark);
      doc.text('Banque :', currX, y);
      currX += doc.getTextWidth('Banque :') + 1;

      const bqStr = (isCheq && bqVal) ? bqVal : '.........';
      doc.setFont('helvetica', isCheq && bqVal ? 'bold' : 'normal');
      doc.setTextColor(...(isCheq && bqVal ? primary : textMuted));
      doc.text(bqStr, currX, y);
      currX += doc.getTextWidth(bqStr) + 2;

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...textDark);
      doc.text('N° :', currX, y);
      currX += doc.getTextWidth('N° :') + 1;

      const numStr = (isCheq && numVal) ? numVal : '.........';
      doc.setFont('helvetica', isCheq && numVal ? 'bold' : 'normal');
      doc.setTextColor(...(isCheq && numVal ? primary : textMuted));
      doc.text(numStr, currX, y);
      currX += doc.getTextWidth(numStr) + 3.5;

      // 4. Versement : Date
      drawBoxWithCheck(currX, y - 2.5, isVers, 'Versement');
      doc.setFont('helvetica', isVers ? 'bold' : 'normal');
      currX += 4.5 + doc.getTextWidth('Versement') + 1.2;

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...textDark);
      doc.text('Date :', currX, y);
      currX += doc.getTextWidth('Date :') + 1;

      const versDateStr = isVers ? formatDate(p.dateVersement || p.datePaiement || p.createdAt) : '...........';
      doc.setFont('helvetica', isVers ? 'bold' : 'normal');
      doc.setTextColor(...(isVers ? primary : textMuted));
      doc.text(versDateStr, currX, y);

      // --- Section Inférieure ---
      // Date
      y = 68.5 + yOffset;
      doc.setTextColor(...textDark);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.text('À : Casablanca, le :', 10, y);
      doc.setFont('helvetica', 'normal');
      doc.text(formatDate(p.datePaiement || p.createdAt || new Date()), 41, y);

      // Case "Édité par ........." (Demande Khadija Finance)
      const userStorageStr = localStorage.getItem('user');
      const currentUser = userStorageStr ? JSON.parse(userStorageStr) : null;
      const currentUserName = (currentUser?.prenom && (currentUser?.nom || currentUser?.nomDeFamille))
        ? `${currentUser.prenom} ${currentUser.nom || currentUser.nomDeFamille}`
        : (currentUser?.nomComplet || currentUser?.nom || currentUser?.prenom || (currentUser?.email ? currentUser.email.split('@')[0] : ''));

      const rawEditorName = p.creeParNom || (p.creePar && typeof p.creePar === 'object' ? (p.creePar.nomComplet || `${p.creePar.prenom || ''} ${p.creePar.nom || ''}`.trim() || p.creePar.nom) : null) || currentUserName;
      const editorName = formatEditorName(rawEditorName, currentUser);

      // Encadré officiel "Édité par"
      doc.setFillColor(243, 244, 246);
      doc.setDrawColor(209, 213, 219);
      doc.setLineWidth(0.4);
      doc.roundedRect(10, 71.5 + yOffset, 78, 6.5, 1, 1, 'FD');

      doc.setTextColor(...primary);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.text('Édité par :', 12, 75.8 + yOffset);

      doc.setTextColor(...textDark);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.text(editorName, 28, 75.8 + yOffset);

      // Avertissement OBLIGATOIRE (Demande utilisateur)
      doc.setTextColor(...accentRed);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.text("NB : Les frais d'inscription ne sont pas remboursables", 10, 83 + yOffset);

      // Administration SUP2I / Signature & Cachet
      doc.setTextColor(...primary);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.text('Administration SUP2I :', 135, 68 + yOffset);

      doc.setDrawColor(...royalBlue);
      doc.setLineWidth(0.5);
      doc.roundedRect(135, 70 + yOffset, 65, 14, 1.5, 1.5, 'D');

      doc.setTextColor(...textMuted);
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(7);
      doc.text('Signature & Cachet', 167.5, 77.5 + yOffset, { align: 'center' });

      // --- BANDEAU INFÉRIEUR (Bleu SUP2I comme sur le reçu papier) ---
      doc.setFillColor(...primary);
      doc.rect(4.5, 87.5 + yOffset, 201, 8.5, 'F');

      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.text("SUP2I  |  Reconnue par l'État", 9, 92 + yOffset);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.2);
      doc.text('1 bld El Yazidi - Ain Sebaa - Casablanca  •  Tél : +212 (0) 522 249 175', 198, 91 + yOffset, { align: 'right' });
      doc.setTextColor(191, 219, 254);
      doc.text('E-mail : infos@sup2i.ac  •  www.sup2i.ac', 198, 94.5 + yOffset, { align: 'right' });
    };

    // Exemplaire 1 (Haut de la page)
    renderReceipt(0);

    // Ligne de découpe au milieu de la page A4 (Y = 120 mm)
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.3);
    doc.setLineDashPattern([2, 2], 0);
    doc.line(4, 120, 206, 120);
    doc.setLineDashPattern([], 0);

    doc.setTextColor(120, 120, 120);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7);
    doc.text('------------------ Découper ici / Cut here ------------------', 105, 119, { align: 'center' });

    // Exemplaire 2 (Bas de la page)
    renderReceipt(138);

    doc.autoPrint();
    try {
      window.open(doc.output('bloburl'), '_blank');
    } catch (e) {
      doc.save(`Recu_${receiptNum}.pdf`);
    }
  };

  // ─── Reset étudiant quand on change d'année ───────────────────────────────
  const resetEtudiant = () => {
    setForm(prev => ({ ...prev, etudiant: '', cours: [] }));
    setPrixTotalEtudiant(0); setTotalDejaPaye(0); setResteAPayer(0);
    setModePaiementEtudiant(''); setInfosModesPaiement(null); setFraisInscription(0);
    setCodeEtudiantSelectionne(''); setAnneeScolaireEtudiant(''); setEtudiantIdPaiement('');
  };

  const handleAnneeChange = (nouvelleAnnee) => {
    setAnneeFiltre(nouvelleAnnee);
    resetEtudiant();
  };

  // ─── Chargement : utilise /paiements/suivi-complet pour avoir TOUS les étudiants ──
  // y compris ceux qui ont été transférés mais ont une dette sur cette année
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const config = getAuthConfig();

        // 1. Cours
        const resCours = await axios.get(`${API_BASE_URL}/cours`, config);
        setCours(resCours.data.map(c => ({ value: c.nom, label: c.nom })));

        // 2. Utiliser suivi-complet → renvoie tous les étudiants de l'année
        //    PLUS les transférés qui ont encore une dette
        const { data: suiviData } = await axios.get(
          `${API_BASE_URL}/paiements/suivi-complet?anneeScolaire=${encodeURIComponent(anneeFiltre)}`,
          config
        );

        // Construire la map id→etudiant et les options du Select
        const map = {};
        const options = [];

        suiviData.forEach(s => {
          // Clé unique pour ce "slot" : l'_id MongoDB de l'enregistrement de cette année
          const id = s._id.toString();
          map[id] = s;

          // Label : nom + code + éventuellement "(transféré → 2026/2027)"
          let label = `${s.nomComplet}`;
          if (s.codeEtudiant) label += ` — ${s.codeEtudiant}`;
          if (s.aTransfere) label += ` 🔄 (transféré → ${s.anneeTransfert})`;
          if (s.reste <= 0) label += ` ✅`;

          options.push({
            value: id,
            label,
            isDisabled: s.reste <= 0 && !s.estInscription, // soldé
            reste: s.reste,
            aTransfere: s.aTransfere,
          });
        });

        // Trier : d'abord ceux qui ont un reste, ensuite les soldés
        options.sort((a, b) => {
          if (a.reste > 0 && b.reste <= 0) return -1;
          if (a.reste <= 0 && b.reste > 0) return 1;
          return 0;
        });

        setEtudiantsComplets(map);
        setEtudiantOptions(options);

      } catch (err) {
        console.error('Erreur chargement données:', err);
        showMessage('❌ Erreur lors du chargement des données', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [anneeFiltre, getAuthConfig, showMessage]);

  // ─── Chargement infos d'un étudiant (pour afficher prix, reste, etc.) ─────
  const loadEtudiantInfo = async (etudiantComplet, etudiantMongoId) => {
    try {
      const config = getAuthConfig();
      const coursFinaux = etudiantComplet?.cours || [];

      try {
        const { data: infos } = await axios.get(
          `${API_BASE_URL}/paiements/etudiant/${etudiantMongoId}/info`,
          config
        );
        setPrixTotalEtudiant(infos.etudiant.prixTotal);
        setTotalDejaPaye(infos.totaux.formation);
        setResteAPayer(infos.totaux.resteAPayer);
        setModePaiementEtudiant(infos.etudiant.modePaiement);
        setInfosModesPaiement(infos.infosModesPaiement);
        setFraisInscription(infos.totaux.inscription || 0);
        setCodeEtudiantSelectionne(etudiantComplet?.codeEtudiant || '');
        setAnneeScolaireEtudiant(etudiantComplet?.anneeScolaire || anneeFiltre);
        setEtudiantIdPaiement(etudiantMongoId);

        if (infos.etudiant.modePaiement !== 'annuel' && infos.prochaineTranche) {
          setForm(prev => ({
            ...prev,
            montant: infos.prochaineTranche.montant.toString(),
            nombreMois: infos.prochaineTranche.nombreMois,
            note: infos.prochaineTranche.description,
            cours: coursFinaux,
          }));
        } else {
          setForm(prev => ({ ...prev, cours: coursFinaux }));
        }
      } catch {
        // Fallback si /info non disponible
        const { data: paiements } = await axios.get(
          `${API_BASE_URL}/paiements/etudiant/${etudiantMongoId}`,
          config
        );
        const totalPaye = paiements.reduce((acc, p) => acc + (p.montant || 0), 0);
        const prixTotal = etudiantComplet?.prixTotal || 0;
        setPrixTotalEtudiant(prixTotal);
        setTotalDejaPaye(totalPaye);
        setResteAPayer(Math.max(0, prixTotal - totalPaye));
        setModePaiementEtudiant(etudiantComplet?.modePaiement || 'semestriel');
        setInfosModesPaiement(null);
        setFraisInscription(0);
        setCodeEtudiantSelectionne(etudiantComplet?.codeEtudiant || '');
        setAnneeScolaireEtudiant(etudiantComplet?.anneeScolaire || anneeFiltre);
        setEtudiantIdPaiement(etudiantMongoId);
        setForm(prev => ({ ...prev, cours: coursFinaux }));
      }
    } catch (err) {
      console.error('Erreur calcul paiements:', err);
      const prixTotal = etudiantComplet?.prixTotal || 0;
      setPrixTotalEtudiant(prixTotal);
      setTotalDejaPaye(0);
      setResteAPayer(prixTotal);
      setModePaiementEtudiant(etudiantComplet?.modePaiement || 'semestriel');
      setInfosModesPaiement(null);
      setFraisInscription(0);
      setCodeEtudiantSelectionne(etudiantComplet?.codeEtudiant || '');
      setAnneeScolaireEtudiant(etudiantComplet?.anneeScolaire || anneeFiltre);
      setEtudiantIdPaiement(etudiantMongoId);
    }
  };

  // ─── Sélection étudiant ───────────────────────────────────────────────────
  const handleEtudiantChange = async (selected) => {
    if (!selected) { resetEtudiant(); return; }

    const etudiantComplet = etudiantsComplets[selected.value];
    setForm(prev => ({ ...prev, etudiant: selected.value }));

    // L'ID MongoDB pour ce "slot" année = selected.value (= _id de l'enregistrement)
    await loadEtudiantInfo(etudiantComplet, selected.value);
  };

  // ─── Type de paiement ─────────────────────────────────────────────────────
  const handleTypePaiementChange = (type) => {
    const isInscription = type === 'inscription';
    setForm(prev => ({
      ...prev,
      typePaiement: type,
      estInscription: isInscription,
      moisDebut: isInscription ? '' : prev.moisDebut,
      nombreMois: isInscription ? 0 : (infosModesPaiement?.moisParTranche || 1),
      montant: isInscription ? '' : (infosModesPaiement?.montantParTranche?.toString() || ''),
      note: isInscription ? "Frais d'inscription" : (infosModesPaiement?.description || ''),
    }));
  };

  // ─── Validation ───────────────────────────────────────────────────────────
  const validateForm = () => {
    if (!form.etudiant) {
      showMessage('❌ Veuillez sélectionner un étudiant', 'error'); return false;
    }
    if (!form.numeroSerie?.trim() || form.numeroSerie.trim().length < 3) {
      showMessage('❌ Le numéro de série est obligatoire (min. 3 caractères)', 'error'); return false;
    }
    if (!form.montant || parseFloat(form.montant) <= 0) {
      showMessage('❌ Le montant doit être supérieur à 0', 'error'); return false;
    }
    if (!form.modePayment) {
      showMessage('❌ Veuillez sélectionner un mode de paiement', 'error'); return false;
    }
    if (!form.datePaiement) {
      showMessage('❌ La date de paiement est obligatoire', 'error'); return false;
    }
    if (isTotalementPaye) {
      showMessage('❌ Cet étudiant a déjà payé la totalité de sa formation', 'error'); return false;
    }
    if (!form.estInscription && resteAPayer > 0 && parseFloat(form.montant) > resteAPayer) {
      showMessage(`❌ Le montant ne peut pas dépasser le reste (${fmt(resteAPayer)})`, 'error');
      return false;
    }
    return true;
  };

  // ─── Soumission ───────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      const config = getAuthConfig();
      const etudiantComplet = etudiantsComplets[form.etudiant];

      const res = await axios.post(`${API_BASE_URL}/paiements`, {
        // ⚠️ On utilise etudiantIdPaiement = l'_id MongoDB de l'enregistrement de cette année
        etudiant: etudiantIdPaiement,
        cours: form.cours,
        moisDebut: form.moisDebut || new Date().toISOString().split('T')[0],
        nombreMois: form.nombreMois,
        montant: parseFloat(form.montant),
        note: form.note,
        estInscription: form.estInscription,
        numeroSerie: form.numeroSerie.trim(),
        numeroSerieManuel: form.numeroSerieManuel ? form.numeroSerieManuel.trim() : '',
        modePayment: form.modePayment,
        banque: form.banque || '',
        numeroCheque: form.numeroCheque || '',
        datePaiement: form.datePaiement,
      }, config);

      const userStorageStr = localStorage.getItem('user');
      const currentUser = userStorageStr ? JSON.parse(userStorageStr) : null;
      const currentUserName = currentUser?.nom ||
        (currentUser?.prenom ? `${currentUser.prenom} ${currentUser.nomDeFamille || ''}`.trim() : '') ||
        currentUser?.nomComplet ||
        (currentUser?.email ? currentUser.email.split('@')[0] : '');

      const savedPaiement = res.data?.paiement || {};
      const paiementPourPDF = {
        ...savedPaiement,
        _id: savedPaiement._id,
        numeroSerie: form.numeroSerie.trim().toUpperCase(),
        numeroSerieManuel: form.numeroSerieManuel ? form.numeroSerieManuel.trim().toUpperCase() : '',
        montant: parseFloat(form.montant),
        cours: form.cours,
        moisDebut: form.moisDebut || new Date(),
        nombreMois: form.nombreMois || 1,
        note: form.note || '',
        modePayment: form.modePayment,
        banque: form.banque || '',
        numeroCheque: form.numeroCheque || '',
        datePaiement: form.datePaiement,
        estInscription: form.estInscription,
        typePaiement: form.typePaiement,
        etudiant: etudiantComplet || res.data?.etudiant || {},
        creeParNom: currentUserName || ''
      };

      setDernierPaiement(paiementPourPDF);

      // 📥 Téléchargement / Impression automatique immédiate du reçu officiel
      generatePDF(paiementPourPDF);

      showMessage(`✅ Paiement enregistré avec succès ! Reçu N° ${paiementPourPDF.numeroSerie} téléchargé.`, 'success');

      // Recharger les infos de cet étudiant
      await loadEtudiantInfo(etudiantComplet, etudiantIdPaiement);

      // Recharger le prochain numéro de série automatique pour le formulaire suivant
      let nextSerie = '';
      try {
        const nextRes = await axios.get(`${API_BASE_URL}/paiements/prochain-numero-serie`, config);
        nextSerie = nextRes.data?.numeroSerie || '';
      } catch (e) {
        console.error('Erreur rechargement numéro de série:', e);
      }

      setForm(prev => ({
        ...prev,
        moisDebut: '', montant: '', note: '',
        typePaiement: 'formation', estInscription: false,
        numeroSerie: nextSerie,
        numeroSerieManuel: '',
        modePayment: null,
        banque: '', numeroCheque: '',
        datePaiement: new Date().toISOString().split('T')[0],
      }));
    } catch (err) {
      const errorMsg = err.response?.data?.error || "Erreur lors de l'ajout du paiement";
      showMessage(`❌ ${errorMsg}`, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Rappel ───────────────────────────────────────────────────────────────
  const handleAjouterRappel = async () => {
    if (!rappelData.dateRappel || !rappelData.note) {
      showMessage('❌ Veuillez remplir tous les champs du rappel', 'error'); return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/rappels`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          etudiant: etudiantIdPaiement,
          cours: Array.isArray(form.cours) ? form.cours[0] : form.cours,
          montantRestant: form.montant,
          note: rappelData.note,
          dateRappel: rappelData.dateRappel,
        }),
      });
      if (res.ok) {
        showMessage('✅ Rappel enregistré avec succès', 'success');
        setShowRappelModal(false);
        setRappelData({ note: '', dateRappel: '' });
      } else {
        showMessage("❌ Erreur lors de l'enregistrement du rappel", 'error');
      }
    } catch {
      showMessage('❌ Erreur serveur lors de l\'ajout du rappel', 'error');
    }
  };

  // ─── Styles ───────────────────────────────────────────────────────────────
  const styles = {
    container: {
      minHeight: '100vh',
      backgroundImage: 'linear-gradient(135deg, #f0f9ff 0%, #a6dbff 25%, #f3e8ff 100%)',
      padding: '20px',
    },
    formContainer: {
      maxWidth: '1200px', margin: '0 auto', backgroundColor: 'white',
      borderRadius: '12px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', padding: '30px',
    },
    title: { fontSize: '2rem', fontWeight: 'bold', color: '#1f2937', textAlign: 'center', marginBottom: '30px' },
    anneeBanner: {
      background: 'linear-gradient(135deg, #1d4ed8, #3b82f6)',
      borderRadius: '10px', padding: '14px 20px', marginBottom: '24px',
      display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap',
    },
    anneeSelect: {
      padding: '8px 14px', borderRadius: '8px', border: 'none',
      fontSize: 14, fontWeight: 700, color: '#1d4ed8', cursor: 'pointer', background: 'white',
    },
    infoPaiement: {
      borderRadius: '12px', padding: '20px', marginBottom: '25px',
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px',
    },
    infoItem: {
      textAlign: 'center', padding: '14px', backgroundColor: 'white',
      borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '1px solid #f3f4f6',
    },
    infoLabel: { fontSize: '11px', color: '#6b7280', marginBottom: '6px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' },
    infoValue: { fontSize: '19px', fontWeight: 'bold', color: '#1f2937' },
    formGrid: { display: 'grid', gap: '25px' },
    formRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '25px' },
    formGroup: { display: 'flex', flexDirection: 'column' },
    label: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' },
    input: { width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
    textarea: { width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', resize: 'vertical', minHeight: '80px', fontFamily: 'inherit', outline: 'none' },
    buttonContainer: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', marginTop: '30px', flexWrap: 'wrap' },
    button: {
      display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#3b82f6', color: 'white',
      border: 'none', padding: '12px 24px', borderRadius: '8px', fontSize: '16px', fontWeight: '500',
      cursor: 'pointer', transition: 'all 0.2s ease', boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
    },
    quickFillButton: { backgroundColor: '#10b981', fontSize: '14px', padding: '8px 16px' },
    message: {
      marginTop: '20px', padding: '15px', borderRadius: '8px', textAlign: 'center',
      fontWeight: '500', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
    },
    messageSuccess: { backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d' },
    messageError: { backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626' },
  };

  const selectStyles = {
    control: (p, s) => ({
      ...p, minHeight: '44px', border: '1px solid #d1d5db', borderRadius: '8px',
      boxShadow: s.isFocused ? '0 0 0 3px rgba(59,130,246,0.1)' : 'none',
      borderColor: s.isFocused ? '#3b82f6' : '#d1d5db',
    }),
    placeholder: (p) => ({ ...p, color: '#9ca3af' }),
    option: (p, s) => ({
      ...p,
      backgroundColor: s.isDisabled ? '#f9fafb' : s.isSelected ? '#eff6ff' : s.isFocused ? '#f0f9ff' : 'white',
      color: s.isDisabled ? '#9ca3af' : '#111827',
      cursor: s.isDisabled ? 'not-allowed' : 'pointer',
    }),
    multiValue: (p) => ({ ...p, backgroundColor: '#e0f2fe', borderRadius: '6px' }),
    multiValueLabel: (p) => ({ ...p, color: '#0369a1' }),
    multiValueRemove: (p) => ({ ...p, color: '#0369a1', '&:hover': { backgroundColor: '#0369a1', color: 'white' } }),
  };

  const etudiantSelectionne = form.etudiant ? etudiantsComplets[form.etudiant] : null;
  const nbAvecDette = etudiantOptions.filter(o => o.reste > 0).length;
  const nbTotal = etudiantOptions.length;

  if (loading) return (
    <div style={styles.container}>
      <Sidebar onLogout={handleLogout} />
      <div style={styles.formContainer}>
        <div style={{ textAlign: 'center', padding: '40px' }}>Chargement des données...</div>
      </div>
    </div>
  );

  return (
    <div style={styles.container}>
      <Sidebar onLogout={handleLogout} />
      <div style={styles.formContainer}>
        <h2 style={styles.title}>Ajouter un Paiement</h2>

        {/* ─── BANDEAU ANNÉE ─── */}
        <div style={styles.anneeBanner}>
          <Calendar size={20} color="white" />
          <span style={{ color: 'white', fontWeight: 700, fontSize: 15 }}>Année scolaire :</span>
          <select value={anneeFiltre} onChange={e => handleAnneeChange(e.target.value)} style={styles.anneeSelect}>
            {ANNEES.map(yr => <option key={yr} value={yr}>{yr}</option>)}
          </select>
          <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, marginLeft: 'auto' }}>
            {nbTotal} étudiant(s) — {nbAvecDette} avec une dette
          </span>
        </div>

        {/* ─── ALERTE si étudiant transféré ─── */}
        {etudiantSelectionne?.aTransfere && (
          <div style={{
            background: '#fff7ed', border: '1px solid #fed7aa',
            borderRadius: 10, padding: '12px 16px', marginBottom: 20,
            display: 'flex', alignItems: 'center', gap: 10, fontSize: 13,
          }}>
            <AlertCircle size={18} style={{ color: '#c2410c', flexShrink: 0 }} />
            <div>
              <strong style={{ color: '#c2410c' }}>Étudiant transféré vers {etudiantSelectionne.anneeTransfert}</strong>
              <br />
              <span style={{ color: '#78350f' }}>
                Ce paiement sera enregistré sur son dossier de l'année <strong>{anneeFiltre}</strong> (dette restante).
              </span>
            </div>
          </div>
        )}

        {/* ─── Infos paiement ─── */}
        {form.etudiant && (
          <div style={styles.infoPaiement}>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>Prix Total</div>
              <div style={styles.infoValue}>{fmt(prixTotalEtudiant)}</div>
            </div>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>Déjà payé</div>
              <div style={{ ...styles.infoValue, color: '#059669' }}>{fmt(totalDejaPaye + fraisInscription)}</div>
            </div>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>Reste à payer</div>
              <div style={{ ...styles.infoValue, color: resteAPayer > 0 ? '#dc2626' : '#059669' }}>
                {resteAPayer > 0 ? fmt(resteAPayer) : '✅ Soldé'}
              </div>
            </div>
            {modePaiementEtudiant && (
              <div style={styles.infoItem}>
                <div style={styles.infoLabel}>Mode paiement</div>
                <div style={styles.infoValue}>
                  {modePaiementEtudiant.charAt(0).toUpperCase() + modePaiementEtudiant.slice(1)}
                </div>
              </div>
            )}
            {anneeScolaireEtudiant && (
              <div style={styles.infoItem}>
                <div style={styles.infoLabel}>Année dossier</div>
                <div style={{ ...styles.infoValue, fontSize: '15px', color: '#1d4ed8' }}>{anneeScolaireEtudiant}</div>
              </div>
            )}
          </div>
        )}

        <div style={styles.formGrid}>
          {/* ─── Étudiant + Classe ─── */}
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                <UserRoundSearch size={16} style={{ color: '#3b82f6' }} />
                Étudiant *
                <span style={{ color: '#6b7280', fontWeight: 400, fontSize: 12 }}>
                  (inscrits {anneeFiltre} + transférés avec dette)
                </span>
              </label>
              <Select
                options={etudiantOptions}
                value={etudiantOptions.find(e => e.value === form.etudiant) || null}
                onChange={handleEtudiantChange}
                placeholder={
                  etudiantOptions.length === 0
                    ? `Aucun étudiant pour ${anneeFiltre}`
                    : 'Rechercher un étudiant...'
                }
                isSearchable
                styles={selectStyles}
                noOptionsMessage={() => `Aucun étudiant trouvé pour ${anneeFiltre}`}
                formatOptionLabel={(opt) => (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{opt.label}</span>
                    {opt.reste > 0 && (
                      <span style={{
                        fontSize: 11, color: '#dc2626', fontWeight: 700,
                        background: '#fef2f2', padding: '1px 6px', borderRadius: 4, marginLeft: 8,
                      }}>
                        {fmt(opt.reste)}
                      </span>
                    )}
                  </div>
                )}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                <BookOpen size={16} style={{ color: '#10b981' }} /> Classe
              </label>
              <Select
                options={cours}
                value={cours.filter(o => form.cours.includes(o.value))}
                onChange={sel => setForm({ ...form, cours: sel ? sel.map(o => o.value) : [] })}
                placeholder="Classe(s) sélectionnée(s)"
                isMulti isSearchable styles={selectStyles} isDisabled
              />
            </div>
          </div>

          {/* ─── Type de paiement ─── */}
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                <BadgeEuro size={16} style={{ color: '#8b5cf6' }} /> Type de paiement *
              </label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="button" onClick={() => handleTypePaiementChange('formation')}
                  style={{
                    ...styles.button, padding: '10px 20px',
                    backgroundColor: form.typePaiement === 'formation' ? '#3b82f6' : '#e5e7eb',
                    color: form.typePaiement === 'formation' ? 'white' : '#374151',
                  }}
                  disabled={!form.etudiant}>
                  Formation
                </button>
                <button type="button" onClick={() => handleTypePaiementChange('inscription')}
                  style={{
                    ...styles.button, padding: '10px 20px',
                    backgroundColor: form.typePaiement === 'inscription' ? '#8b5cf6' : '#e5e7eb',
                    color: form.typePaiement === 'inscription' ? 'white' : '#374151',
                  }}
                  disabled={!form.etudiant}>
                  Frais d'inscription
                </button>
              </div>
            </div>

            {infosModesPaiement && form.typePaiement === 'formation' && (
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <Info size={16} style={{ color: '#6b7280' }} /> Informations mode
                </label>
                <div style={{ ...styles.input, backgroundColor: '#f9fafb', fontSize: '12px', color: '#374151' }}>
                  <strong>{infosModesPaiement.description}</strong><br />
                  Montant / tranche : <strong>{fmt(infosModesPaiement.montantParTranche)}</strong><br />
                  Mois / tranche : <strong>{infosModesPaiement.moisParTranche}</strong>
                </div>
              </div>
            )}
          </div>

          {/* ─── Dates ─── */}
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                <Calendar size={16} style={{ color: '#06b6d4' }} /> Date du paiement *
              </label>
              <input type="date" value={form.datePaiement}
                onChange={e => setForm({ ...form, datePaiement: e.target.value })}
                style={styles.input} />
            </div>
            {!form.estInscription && (
              <>
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    <Calendar size={16} style={{ color: '#8b5cf6' }} /> Date de début
                  </label>
                  <input type="date" value={form.moisDebut}
                    onChange={e => setForm({ ...form, moisDebut: e.target.value })}
                    style={styles.input} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    <Calendar size={16} style={{ color: '#f59e0b' }} /> Nombre de mois
                  </label>
                  <input type="number" value={form.nombreMois} min="1"
                    onChange={e => setForm({ ...form, nombreMois: parseInt(e.target.value) || 1 })}
                    style={styles.input} readOnly={!!infosModesPaiement} />
                </div>
              </>
            )}
          </div>

          {/* ─── Numéro de série + Montant ─── */}
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                <StickyNote size={16} style={{ color: '#dc2626' }} /> Numéro de série *
                <span style={{
                  fontSize: '11px',
                  color: '#1d4ed8',
                  background: '#dbeafe',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  marginLeft: '8px',
                  fontWeight: '600',
                  border: '1px solid #bfdbfe'
                }}>
                  Automatique
                </span>
              </label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <input
                  type="text"
                  required
                  value={form.numeroSerie || ''}
                  onChange={e => setForm({ ...form, numeroSerie: e.target.value })}
                  placeholder="sup00001"
                  style={{
                    ...styles.input,
                    paddingRight: '38px',
                    fontFamily: 'monospace',
                    fontWeight: '700',
                    fontSize: '15px',
                    letterSpacing: '0.5px'
                  }}
                />
                <button
                  type="button"
                  onClick={fetchProchainNumeroSerie}
                  title="Actualiser le numéro de série automatique"
                  style={{
                    position: 'absolute',
                    right: '8px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#4b5563',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <RefreshCw size={15} />
                </button>
              </div>
              <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px' }}>
                <AlertCircle size={12} style={{ display: 'inline', marginRight: '4px' }} />
                Généré automatiquement (modifiable et obligatoire)
              </small>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                <BadgeEuro size={16} style={{ color: '#10b981' }} />
                Montant (MAD) *
                {resteAPayer > 0 && !form.estInscription && (
                  <button type="button"
                    onClick={() => setForm(prev => ({ ...prev, montant: resteAPayer.toString() }))}
                    style={{ ...styles.button, ...styles.quickFillButton, marginLeft: '8px' }}>
                    Reste : {fmt(resteAPayer)}
                  </button>
                )}
              </label>
              <input type="number" value={form.montant}
                onChange={e => setForm({ ...form, montant: e.target.value })}
                placeholder="0.00" min="0" step="0.01"
                disabled={isTotalementPaye}
                style={{
                  ...styles.input,
                  backgroundColor: isTotalementPaye ? '#f3f4f6' : '#ffffff',
                  cursor: isTotalementPaye ? 'not-allowed' : 'text'
                }} />
              {isTotalementPaye && (
                <small style={{ color: '#059669', marginTop: '4px' }}>✅ Cet étudiant a payé la totalité</small>
              )}
            </div>
          </div>

          {/* ─── Mode de paiement ─── */}
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                <CreditCard size={16} style={{ color: '#f59e0b' }} /> Mode de paiement *
              </label>
              <Select
                options={modePaymentOptions}
                value={modePaymentOptions.find(o => o.value === form.modePayment) || null}
                onChange={sel => setForm({ ...form, modePayment: sel ? sel.value : null })}
                placeholder="Sélectionner un mode de paiement"
                isSearchable styles={selectStyles}
              />
              {form.modePayment && (
                <small style={{ color: '#059669', marginTop: '4px' }}>
                  ✅ Mode sélectionné : {form.modePayment}
                </small>
              )}
            </div>

            {form.modePayment?.toLowerCase().includes('ch') && (
              <>
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    <CreditCard size={16} style={{ color: '#3b82f6' }} /> Banque (Chèque)
                  </label>
                  <input
                    type="text"
                    value={form.banque || ''}
                    onChange={e => setForm({ ...form, banque: e.target.value })}
                    placeholder="Ex: Attijariwafa Bank, BMCE, CIH..."
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    <StickyNote size={16} style={{ color: '#3b82f6' }} /> Numéro de Chèque
                  </label>
                  <input
                    type="text"
                    value={form.numeroCheque || ''}
                    onChange={e => setForm({ ...form, numeroCheque: e.target.value })}
                    placeholder="Ex: 0048291"
                    style={styles.input}
                  />
                </div>
              </>
            )}
          </div>

          {/* ─── Note ─── */}
          <div style={styles.formGroup}>
            <label style={styles.label}>
              <StickyNote size={16} style={{ color: '#eab308' }} /> Note (optionnel)
            </label>
            <textarea value={form.note}
              onChange={e => setForm({ ...form, note: e.target.value })}
              placeholder="Ajouter une note..." style={styles.textarea} />
          </div>

          {/* ─── Boutons ─── */}
          <div style={styles.buttonContainer}>
            <button onClick={handleSubmit}
              style={{
                ...styles.button,
                opacity: submitting || isTotalementPaye ? 0.5 : 1,
                cursor: submitting || isTotalementPaye ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              disabled={submitting || isTotalementPaye}>
              <Download size={18} />
              {submitting ? 'Enregistrement...' : 'Enregistrer le paiement & Télécharger le reçu'}
            </button>

            {dernierPaiement && (
              <button
                type="button"
                onClick={() => generatePDF(dernierPaiement)}
                style={{
                  ...styles.button,
                  backgroundColor: '#0284c7',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                title="Télécharger à nouveau le reçu officiel"
              >
                <Download size={18} />
                Télécharger le reçu ({dernierPaiement.numeroSerie})
              </button>
            )}

            <ImportPaiementsExcel
              API_BASE_URL={API_BASE_URL}
              getAuthConfig={getAuthConfig}
              onImportSuccess={() => window.location.reload()}
            />

            <button onClick={() => setShowRappelModal(true)}
              style={{ ...styles.button, backgroundColor: '#8b5cf6' }}
              disabled={!form.etudiant}>
              Ajouter un rappel
            </button>
          </div>
        </div>

        {/* ─── Historique dépliable ─── */}
        {form.etudiant && codeEtudiantSelectionne && (
          <HistoriqueEtudiant
            codeEtudiant={codeEtudiantSelectionne}
            getAuthConfig={getAuthConfig}
            anneeCourante={anneeScolaireEtudiant}
          />
        )}

        {/* ─── Message ─── */}
        {message.text && (
          <div style={{
            ...styles.message,
            ...(message.type === 'error' ? styles.messageError : styles.messageSuccess),
          }}>
            {message.type === 'error' ? <AlertCircle size={20} /> : <Info size={20} />}
            {message.text}
          </div>
        )}
      </div>

      {/* ─── Modal Rappel ─── */}
      {showRappelModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: 'white', padding: '30px', borderRadius: '12px',
            width: '90%', maxWidth: '500px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
          }}>
            <h2 style={{ marginBottom: '20px', fontSize: '20px', fontWeight: 'bold' }}>
              Ajouter un rappel de paiement
            </h2>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Description *</label>
              <textarea value={rappelData.note}
                onChange={e => setRappelData({ ...rappelData, note: e.target.value })}
                rows="4"
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db', fontFamily: 'inherit', fontSize: '14px' }} />
            </div>
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Date du rappel *</label>
              <input type="date" value={rappelData.dateRappel}
                onChange={e => setRappelData({ ...rappelData, dateRappel: e.target.value })}
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button onClick={() => { setShowRappelModal(false); setRappelData({ note: '', dateRappel: '' }); }}
                style={{ padding: '10px 16px', background: '#e5e7eb', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500' }}>
                Annuler
              </button>
              <button onClick={handleAjouterRappel}
                style={{ padding: '10px 16px', background: '#059669', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500' }}>
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AjouterPaiement;